<!--
  CompanySpotlight — the admin company switcher as a full-screen, Spotlight-style
  overlay: one big search field, big logos, and agency-owned companies nested
  under their agency so you can jump to the agency portal or straight into any
  company it owns.

  Only mounted while open (v-if on the backdrop), which matters: every logo row
  resolves a signed Bunny URL through a callable.
-->
<template>
  <!-- No <transition>: the navbar unmounts on some route changes, and a leave
       transition in flight strands the teleported node in <body>. Entry is a
       plain CSS animation instead. -->
  <Teleport to="body">
    <div v-if="open" class="spotlight-backdrop" @click.self="$emit('close')">
      <div class="spotlight-panel">
        <div class="spotlight-search">
          <span class="icon has-text-grey">
            <font-awesome-icon :icon="['fal', 'magnifying-glass']" />
          </span>
          <input
            ref="inputRef"
            v-model="query"
            type="text"
            class="spotlight-input"
            :placeholder="isNexus ? 'Log into a company…' : 'Switch company…'"
            @keydown.down.prevent="move(1)"
            @keydown.up.prevent="move(-1)"
            @keydown.left.prevent="actionIndex = 0"
            @keydown.right.prevent="actionIndex = 1"
            @keydown.enter.prevent="choose"
            @keydown.esc.prevent="$emit('close')"
          />
        </div>

        <div v-if="rows.length" class="spotlight-results">
          <template v-for="(row, i) in rows" :key="row.key">
            <div v-if="row.type === 'header'" class="spotlight-header">
              {{ row.label }}
            </div>
            <a
              v-else
              :ref="(el) => setRowRef(el, i)"
              class="spotlight-row"
              :class="{
                'is-active': i === cursor,
                'is-child': row.depth > 0,
                'is-dim': row.item.active === false,
                'in-group': row.group,
                'is-group-head': row.group === 'head',
                'is-group-last': row.group === 'last',
              }"
              @click="pick(row.item)"
            >
              <span class="spotlight-plate">
                <BunnyImage
                  :asset-id="row.item.logoAssetId"
                  :path="row.item.logoRef"
                  :fallback="row.item.logoUrl"
                  :alt="row.item.name"
                  img-class="spotlight-logo-img"
                >
                  <template #placeholder>
                    <font-awesome-icon
                      class="has-text-grey-light"
                      :icon="['fal', row.item.isAgency ? 'buildings' : 'building']"
                    />
                  </template>
                </BunnyImage>
              </span>
              <span class="spotlight-name">
                {{ row.item.name }}
                <span v-if="row.item.isAgency" class="spotlight-badge">Agency</span>
                <span v-if="row.item.id === currentId" class="spotlight-badge is-current">
                  Current
                </span>
              </span>
              <!-- An agency has two destinations: the portal where it manages
                   its clients, and its own company account. -->
              <span v-if="row.item.isAgency" class="spotlight-actions">
                <button
                  class="spotlight-action"
                  :class="{ 'is-armed': i === cursor && actionIndex === 0 }"
                  @click.stop="pick(row.item)"
                >
                  Agency Portal
                </button>
                <button
                  class="spotlight-action"
                  :class="{ 'is-armed': i === cursor && actionIndex === 1 }"
                  @click.stop="pickCompany(row.item)"
                >
                  Company
                </button>
              </span>
              <span v-if="i === cursor" class="icon spotlight-enter has-text-grey">
                <font-awesome-icon :icon="['fal', 'arrow-turn-down-left']" />
              </span>
            </a>
          </template>
        </div>
        <div v-else class="spotlight-empty">
          {{ query ? `No companies match “${query}”` : 'No companies yet.' }}
        </div>

        <div class="spotlight-foot">
          <span class="spotlight-hint">
            ↑↓ navigate ·
            <template v-if="cursorIsAgency">←→ portal / company · </template>
            ↵ open · esc close
          </span>
          <slot name="footer">
            <button
              v-if="!isNexus"
              class="button is-danger is-small"
              @click="$emit('stop-impersonation')"
            >
              <span class="icon">
                <font-awesome-icon :icon="['fas', 'person-from-portal']" />
              </span>
              <span>Back to Nexus</span>
            </button>
          </slot>
        </div>
    </div>
  </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import BunnyImage from '@/components/common/BunnyImage.vue'

const props = defineProps({
  open: { type: Boolean, default: false },
  // Switcher items from useNavbarState: { id, name, active, isAgency, agencyId,
  // logoAssetId, logoUrl }
  companies: { type: Array, default: () => [] },
  currentId: { type: String, default: null },
  isNexus: { type: Boolean, default: true },
})

const emit = defineEmits(['select', 'close', 'stop-impersonation'])

const query = ref('')
const cursor = ref(0)
// Which of an agency row's two destinations ←/→ has armed: 0 portal, 1 company.
const actionIndex = ref(0)
const inputRef = ref(null)
const rowEls = ref([])
const setRowRef = (el, i) => {
  if (el) rowEls.value[i] = el
}

const byName = (a, b) => a.name.localeCompare(b.name)

const rows = computed(() => {
  const q = query.value.trim().toLowerCase()
  const hit = (c) => !q || c.name.toLowerCase().includes(q)
  const all = props.companies
  const kidsOf = (id) => all.filter((c) => c.agencyId === id).sort(byName)

  // One list, agencies in their alphabetical place among everyone else — an
  // agency just brings its owned companies along, nested underneath it.
  const push = (out, c) => {
    if (!c.isAgency) {
      if (hit(c)) out.push({ type: 'row', key: `c-${c.id}`, item: c, depth: 0 })
      return
    }
    const kids = hit(c) ? kidsOf(c.id) : kidsOf(c.id).filter(hit)
    if (!hit(c) && !kids.length) return
    // `group` brackets the agency and its companies into one tinted block:
    // head / mid / last drive the block's rounding and rail.
    out.push({ type: 'row', key: `c-${c.id}`, item: c, depth: 0, group: 'head' })
    kids.forEach((kid, n) =>
      out.push({
        type: 'row',
        key: `k-${kid.id}`,
        item: kid,
        depth: 1,
        group: n === kids.length - 1 ? 'last' : 'mid',
      }),
    )
  }

  // Agency-owned companies are listed under their agency, never again at top
  // level — but only when that agency is actually in this list.
  const parents = new Set(all.filter((c) => c.isAgency).map((c) => c.id))
  const top = all.filter((c) => !c.agencyId || !parents.has(c.agencyId)).sort(byName)
  const out = []
  top.filter((c) => c.active !== false).forEach((c) => push(out, c))

  const inactive = []
  top.filter((c) => c.active === false).forEach((c) => push(inactive, c))
  if (inactive.length) {
    out.push({ type: 'header', key: 'h-inactive', label: 'Inactive' })
    out.push(...inactive)
  }
  return out
})

const cursorIsAgency = computed(() => !!rows.value[cursor.value]?.item?.isAgency)

const firstRowIndex = () => rows.value.findIndex((r) => r.type === 'row')

const move = (step) => {
  const list = rows.value
  let i = cursor.value
  for (let n = 0; n < list.length; n++) {
    i = (i + step + list.length) % list.length
    if (list[i]?.type === 'row') break
  }
  cursor.value = i
  actionIndex.value = 0
  nextTick(() => rowEls.value[i]?.scrollIntoView({ block: 'nearest' }))
}

// An agency row's default (click / ↵) is its portal; `pickCompany` is the
// second button — same company doc, routed as an ordinary client account.
// Close on the way out rather than waiting for the parent's async switch to
// finish — the overlay should never outlive the click that used it.
const pick = (item) => {
  emit('select', item)
  emit('close')
}
const pickCompany = (item) => pick({ ...item, isAgency: false })

const choose = () => {
  const row = rows.value[cursor.value]
  if (row?.type !== 'row') return
  if (row.item.isAgency && actionIndex.value === 1) pickCompany(row.item)
  else pick(row.item)
}

// Typing resets the highlight to the best match; opening focuses the field.
watch(rows, () => {
  if (rows.value[cursor.value]?.type !== 'row') cursor.value = firstRowIndex()
})
watch(query, () => {
  cursor.value = firstRowIndex()
  actionIndex.value = 0
})
watch(
  () => props.open,
  (open) => {
    if (!open) return
    query.value = ''
    rowEls.value = []
    cursor.value = firstRowIndex()
    nextTick(() => inputRef.value?.focus())
  },
)
</script>

<style lang="scss" scoped>
.spotlight-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 14vh 1rem 2rem;
  background: rgba(10, 10, 10, 0.45);
  backdrop-filter: blur(6px);
}

.spotlight-panel {
  // Tint for an agency block — reads on both schemes without hardcoding a grey.
  --group-tint: color-mix(in srgb, var(--bulma-primary) 7%, transparent);
  width: 100%;
  max-width: 720px;
  display: flex;
  flex-direction: column;
  max-height: 70vh;
  background: var(--bulma-scheme-main, #fff);
  border: 1px solid var(--bulma-border);
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(10, 10, 10, 0.35);
  overflow: hidden;
  animation: spotlight-in 0.12s ease;
}

@keyframes spotlight-in {
  from {
    opacity: 0;
    transform: translateY(-8px) scale(0.99);
  }
}

.spotlight-search {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.15rem 1.5rem;
  border-bottom: 1px solid var(--bulma-border);

  .icon {
    font-size: 1.25rem;
  }
}

.spotlight-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 1.6rem;
  color: var(--bulma-text-strong, #1f1f1f);
  outline: none;

  &::placeholder {
    color: var(--bulma-text-weak, #9a9a9a);
  }
}

.spotlight-results {
  overflow-y: auto;
  padding: 0.5rem 0.75rem 0.75rem;
}

.spotlight-header {
  padding: 0.75rem 0.75rem 0.35rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--bulma-text-weak, #9a9a9a);
}

.spotlight-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.7rem 0.85rem;
  border-radius: 8px;
  color: var(--bulma-text, #363636);
  cursor: pointer;

  // Breathing room does the separating — no hairlines. Rows inside an agency
  // block stay flush, since that block is meant to read as one object.
  & + .spotlight-row:not(.in-group) {
    margin-top: 0.3rem;
  }

  // The highlight is a hard accent edge, not a pale wash — it has to survive
  // sitting on top of an agency block's own tint.
  &:hover:not(.is-active) {
    background: color-mix(in srgb, var(--bulma-scheme-invert) 6%, transparent);
  }

  &.is-active {
    background: var(--bulma-primary-light);
    color: var(--bulma-primary-dark);
    box-shadow: inset 3px 0 0 var(--bulma-primary);
  }

  &.is-dim {
    .spotlight-name {
      color: var(--bulma-text-weak, #9a9a9a);
    }

    .spotlight-plate {
      opacity: 0.6;
    }
  }

  // ── Agency block: the agency and everything it owns read as one unit ──
  &.in-group {
    background: var(--group-tint);
    border-radius: 0;

    &.is-active {
      background: var(--bulma-primary-light);
      box-shadow: inset 3px 0 0 var(--bulma-primary);
    }
  }

  &.is-group-head {
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    margin-top: 0.55rem;
  }

  &.is-group-last {
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
    margin-bottom: 0.55rem;
  }

  // Owned companies sit inside the block, stepped in behind a second rail.
  &.is-child {
    padding-left: 2.4rem;

    &::before {
      content: '';
      position: absolute;
      left: 1.5rem;
      top: 0;
      bottom: 0;
      width: 1px;
      background: var(--bulma-border);
    }

    .spotlight-plate {
      flex-basis: 72px;
      width: 72px;
      height: 32px;
    }

    .spotlight-name {
      font-size: 0.95rem;
      font-weight: 400;
    }
  }
}

// Most company logos are wide wordmarks, so the well is a letterbox plate:
// they render at their real aspect ratio instead of being squeezed into a
// square, and every row's text still starts on the same vertical line.
.spotlight-plate {
  flex: 0 0 92px;
  width: 92px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.4rem;
  border: 1px solid var(--bulma-border);
  border-radius: 6px;
  background: var(--bulma-scheme-main, #fff);
  font-size: 1.1rem;
  overflow: hidden;

  :deep(.spotlight-logo-img) {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

.spotlight-name {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.05rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.spotlight-badge {
  flex: none;
  font-size: 0.65rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--bulma-text-weak, #9a9a9a);
  border: 1px solid var(--bulma-border);
  border-radius: 4px;
  padding: 0.1rem 0.4rem;

  &.is-current {
    color: var(--bulma-primary);
    border-color: var(--bulma-primary);
  }
}

// Segmented control: one agency, two destinations. ←/→ arms one of them.
.spotlight-actions {
  flex: none;
  display: flex;
  border: 1px solid var(--bulma-border);
  border-radius: 6px;
  overflow: hidden;
  background: var(--bulma-scheme-main, #fff);
}

.spotlight-action {
  border: 0;
  background: transparent;
  color: var(--bulma-text, #363636);
  font-family: inherit;
  font-size: 0.7rem;
  letter-spacing: 0.02em;
  padding: 0.35rem 0.7rem;
  cursor: pointer;

  & + & {
    border-left: 1px solid var(--bulma-border);
  }

  &:hover {
    background: color-mix(in srgb, var(--bulma-primary) 20%, transparent);
  }

  &.is-armed {
    background: var(--bulma-primary);
    color: #fff;
  }
}

.spotlight-enter {
  flex: none;
}

.spotlight-empty {
  padding: 2rem;
  text-align: center;
  color: var(--bulma-text-weak, #9a9a9a);
}

.spotlight-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.6rem 1rem;
  border-top: 1px solid var(--bulma-border);
  background: var(--bulma-scheme-main-bis, #fafafa);
}

.spotlight-hint {
  font-size: 0.7rem;
  color: var(--bulma-text-weak, #9a9a9a);
}

@media screen and (max-width: 768px) {
  .spotlight-backdrop {
    padding: 8vh 0.75rem 1rem;
  }

  .spotlight-panel {
    max-height: 80vh;
  }

  .spotlight-input {
    font-size: 1.15rem;
  }

  // Actions drop under the name on narrow screens rather than crushing it.
  .spotlight-row {
    flex-wrap: wrap;
  }

  .spotlight-plate {
    flex-basis: 72px;
    width: 72px;
  }
}
</style>
